/**
 * specEngine.js — pure resolution engine for the spec builder.
 *
 * Faithfully interprets the Documint template language so master section fragments
 * lift in verbatim:
 *   - {{variable}}                         simple interpolation
 *   - {{path.to.value}} / {{a.0.b}}        nested path (numeric segments index arrays)
 *   - {{$md value}}                        markdown passthrough (renders the value)
 *   - {{$var …}} / {{$group …}} / {{$filter …}}   setup helpers → render to nothing
 *   - {{#compare [field] "op" value}} … {{else}} … {{/compare}}   conditional blocks
 *
 * Operators (exact Documint set): ==, !=, isNotEmpty, isNotNull, contains
 *
 * Two layers:
 *   - evalRule(rule, ctx)         structured inclusion logic (queryable visibility_rule)
 *   - renderTemplate(text, ctx)   body text with inline {{#compare}} + interpolation
 *   - renderSchedule(cfg, rows)   Documint repeating feature: group/filter child rows → table data
 *
 * No imports, no side effects. Used by the builder (live preview + authoritative build),
 * the master-library preview, and schedule rendering. Resolved output is persisted to
 * spec_sections and snapshotted on issuance; the PDF reads snapshots (engine is client-only).
 */

/* ──────────────────────────────────────────────────────────────────────────
 * Context path resolution
 * ────────────────────────────────────────────────────────────────────────── */

/**
 * Resolve a dotted path against a context object. Numeric segments index arrays
 * (or numeric object keys), so Documint paths like
 *   project_concepts.0.project.0.project_name
 * resolve directly without rewriting the seed fragments.
 */
export function getPath(ctx, path) {
  if (path == null) return undefined
  const segs = String(path).split('.').map(s => s.trim()).filter(Boolean)
  let cur = ctx
  for (const seg of segs) {
    if (cur == null) return undefined
    cur = cur[seg]
  }
  return cur
}

/* ──────────────────────────────────────────────────────────────────────────
 * Value normalization + comparison
 * ────────────────────────────────────────────────────────────────────────── */

function strip(v) { return v == null ? '' : String(v).trim() }

/** Unquote a literal: 'True' "True" → True; null → null; bare → as-is. */
function unquote(raw) {
  if (raw == null) return null
  let s = String(raw).trim()
  if (s === 'null') return null
  if ((s.startsWith('"') && s.endsWith('"')) || (s.startsWith("'") && s.endsWith("'"))) {
    return s.slice(1, -1)
  }
  return s
}

/** Is a field value "empty" — null/undefined/''/[] / all-blank array. */
function isEmptyVal(v) {
  if (v == null) return true
  if (Array.isArray(v)) return v.filter(x => strip(x) !== '').length === 0
  return strip(v) === ''
}

/** Does fieldVal "contain" needle — substring for strings, membership for arrays. */
function containsVal(fieldVal, needle) {
  const n = strip(unquote(needle))
  if (Array.isArray(fieldVal)) {
    return fieldVal.some(x => strip(x).toLowerCase().includes(n.toLowerCase()))
  }
  return strip(fieldVal).toLowerCase().includes(n.toLowerCase())
}

/**
 * Evaluate one comparison. fieldVal is the resolved context value; op is the
 * Documint operator; value is the (possibly quoted) literal from the template.
 */
export function evalCompare(fieldVal, op, value) {
  switch (op) {
    case '==':          return strip(fieldVal).toLowerCase() === strip(unquote(value)).toLowerCase()
    case '!=':          return strip(fieldVal).toLowerCase() !== strip(unquote(value)).toLowerCase()
    case 'isNotEmpty':  return !isEmptyVal(fieldVal)
    case 'isEmpty':     return isEmptyVal(fieldVal)
    case 'isNotNull':   return fieldVal != null
    case 'isNull':      return fieldVal == null
    case 'contains':    return containsVal(fieldVal, value)
    case 'notContains': return !containsVal(fieldVal, value)
    default:            return false
  }
}

/* ──────────────────────────────────────────────────────────────────────────
 * Structured rule evaluation (visibility_rule)
 *   rule := { all:[rule…] } | { any:[rule…] } | { not:rule }
 *         | { field, op, value }
 * ────────────────────────────────────────────────────────────────────────── */

export function evalRule(rule, ctx) {
  if (!rule) return true
  if (Array.isArray(rule.all)) return rule.all.every(r => evalRule(r, ctx))
  if (Array.isArray(rule.any)) return rule.any.some(r => evalRule(r, ctx))
  if (rule.not) return !evalRule(rule.not, ctx)
  if (rule.field) return evalCompare(getPath(ctx, rule.field), rule.op, rule.value)
  return true
}

/* ──────────────────────────────────────────────────────────────────────────
 * Compare-tag argument parser  →  { field, op, value }
 *   "[allowances] \"isNotEmpty\" null"
 *   "[06_finish_wood_scope] \"contains\" \"Casework\""
 *   "[project_concepts].[0].[project].[0].[project_state] \"!=\" \"California\""
 * Exported so the importer can lift each section's outer compare into a
 * structured visibility_rule with zero rewriting.
 * ────────────────────────────────────────────────────────────────────────── */

const OPS = ['isNotEmpty', 'isEmpty', 'isNotNull', 'isNull', 'notContains', 'contains', '==', '!=']

export function parseCompareArgs(argStr) {
  const s = String(argStr).trim()
  // Field: leading run of bracketed segments [a].[0].[b]  OR a bare token.
  let field, rest
  if (s.startsWith('[')) {
    // consume consecutive [seg] (optionally joined by '.')
    const segs = []
    let i = 0
    while (i < s.length && (s[i] === '[' || s[i] === '.')) {
      if (s[i] === '.') { i++; continue }
      const close = s.indexOf(']', i)
      if (close === -1) break
      segs.push(s.slice(i + 1, close))
      i = close + 1
    }
    field = segs.filter(x => x !== '').join('.')
    rest = s.slice(i).trim()
  } else {
    const m = s.match(/^(\S+)\s*(.*)$/)
    field = m ? m[1] : s
    rest = m ? m[2] : ''
  }
  // Operator: first quoted token.
  const opMatch = rest.match(/^["']([^"']+)["']\s*(.*)$/)
  let op = opMatch ? opMatch[1] : rest.split(/\s+/)[0]
  let value = opMatch ? opMatch[2].trim() : ''
  // Guard against operator not being quoted: detect a known op at the start.
  if (!opMatch) {
    const found = OPS.find(o => rest.startsWith(o))
    if (found) { op = found; value = rest.slice(found.length).trim() }
  }
  if (value === '') value = null
  return { field, op, value: value == null ? null : unquote(value) }
}

/* ──────────────────────────────────────────────────────────────────────────
 * Reference extraction — which answer-field keys does a piece of content use?
 * Powers (a) the authoring gate ("answer these questions before adding this
 * section") and (b) answer-change impact analysis (which sections a changed
 * answer touches). Precise per Matt's spec: if a question customizes a section
 * — via its visibility rule or its text — it "relates."
 * ────────────────────────────────────────────────────────────────────────── */

// Recursively collect `field` references from a visibility rule tree.
export function collectRuleFields(rule, out) {
  out = out || new Set()
  if (!rule || typeof rule !== 'object') return out
  if (rule.field) out.add(String(rule.field).split('.')[0])
  for (const k of ['all', 'any']) if (Array.isArray(rule[k])) rule[k].forEach(r => collectRuleFields(r, out))
  if (rule.not) collectRuleFields(rule.not, out)
  return out
}

// Collect variable references from a template string ({{var}}, {{path.to.x}}, {{#compare field …}},
// {{$md value}}). Returns the first path segment of each (the field key). Skips block close/else and
// pure helpers that don't reference a field.
export function collectTemplateFields(tpl, out) {
  out = out || new Set()
  if (!tpl || typeof tpl !== 'string') return out
  const re = /\{\{\s*([^}]+?)\s*\}\}/g
  let m
  while ((m = re.exec(tpl))) {
    const inner = m[1].trim()
    if (inner.startsWith('#compare')) {
      const { field } = parseCompareArgs(inner.replace(/^#compare\s*/, ''))
      if (field) out.add(String(field).split('.')[0])
      continue
    }
    if (inner.startsWith('/') || inner === 'else') continue
    if (inner.startsWith('$')) {
      const parts = inner.split(/\s+/)
      if (parts[0] === '$md' && parts[1]) out.add(parts[1].split('.')[0])  // {{$md field}} renders a value
      continue
    }
    out.add(inner.split(/\s+/)[0].split('.')[0])
  }
  return out
}

// All keys a master section references (visibility rule + body + client-action templates, and any
// provided line templates/rules). When answerKeySet is given, filters to actual questions — dropping
// derived values and project-context paths (e.g. project_concepts.0…). `lines` is optional
// [{ text_template, visibility_rule }] for line-level sections.
export function sectionReferencedKeys(section, answerKeySet, lines) {
  const out = new Set()
  if (section) {
    collectRuleFields(section.visibility_rule, out)
    collectTemplateFields(section.body_template, out)
    collectTemplateFields(section.client_action_template, out)
  }
  for (const l of (lines || [])) {
    collectRuleFields(l && l.visibility_rule, out)
    collectTemplateFields(l && l.text_template, out)
  }
  if (!answerKeySet) return out
  const filtered = new Set()
  for (const k of out) if (answerKeySet.has(k)) filtered.add(k)
  return filtered
}

/* ──────────────────────────────────────────────────────────────────────────
 * Template tokenizer + parser (handles nested {{#compare}} blocks)
 * ────────────────────────────────────────────────────────────────────────── */

// Private-use sentinels used to mark interpolated {{variable}} values for the editor's
// live-highlight display (opts.markVars). They never appear in normal content, so the
// Markdown renderer can safely turn them into a highlighted (clickable) span. The marked
// payload is VAR_OPEN + variableKey + VAR_SEP + resolvedValue + VAR_CLOSE, so the editor
// can route a click on the value back to the setting that produced it. Default rendering
// (snapshots, PDF, build payload) does NOT emit them.
export const VAR_OPEN = String.fromCharCode(0xE000)
export const VAR_CLOSE = String.fromCharCode(0xE001)
export const VAR_SEP = String.fromCharCode(0xE002)

const MUSTACHE = /\{\{([^}]*)\}\}/g

function tokenize(tpl) {
  const tokens = []
  let last = 0, m
  MUSTACHE.lastIndex = 0
  while ((m = MUSTACHE.exec(tpl)) !== null) {
    if (m.index > last) tokens.push({ t: 'text', v: tpl.slice(last, m.index) })
    const inner = m[1].trim()
    if (inner.startsWith('#compare')) {
      tokens.push({ t: 'open', args: inner.slice('#compare'.length).trim() })
    } else if (inner === '/compare') {
      tokens.push({ t: 'close' })
    } else if (inner === 'else') {
      tokens.push({ t: 'else' })
    } else {
      tokens.push({ t: 'mustache', v: inner })
    }
    last = m.index + m[0].length
  }
  if (last < tpl.length) tokens.push({ t: 'text', v: tpl.slice(last) })
  return tokens
}

// Parse a flat token list into a node tree. Returns [nodes, nextIndex].
function parseNodes(tokens, start) {
  const nodes = []
  let i = start
  while (i < tokens.length) {
    const tk = tokens[i]
    if (tk.t === 'close' || tk.t === 'else') return [nodes, i]
    if (tk.t === 'open') {
      const [children, afterChildren] = parseNodes(tokens, i + 1)
      let elseChildren = null
      let j = afterChildren
      if (tokens[j] && tokens[j].t === 'else') {
        const [ec, afterElse] = parseNodes(tokens, j + 1)
        elseChildren = ec
        j = afterElse
      }
      // j should be the matching close
      nodes.push({ t: 'compare', args: tk.args, children, elseChildren })
      i = (tokens[j] && tokens[j].t === 'close') ? j + 1 : j
    } else {
      nodes.push(tk)
      i++
    }
  }
  return [nodes, i]
}

// opts.markVars: wrap each non-empty interpolated value in VAR_OPEN…VAR_CLOSE so the
// editor can highlight filled-in variables. Off by default (snapshots/PDF/build stay clean).
function resolveMustache(expr, ctx, opts) {
  const e = expr.trim()
  if (!e) return ''
  // setup helpers produce no output
  if (e.startsWith('$var') || e.startsWith('$group') || e.startsWith('$filter')) return ''
  const key = e.startsWith('$md') ? e.slice('$md'.length).trim() : e
  const v = getPath(ctx, key)
  if (v == null) return ''
  const s = String(v)
  if (s === '') return ''
  // markVars: VAR_OPEN + key + VAR_SEP + value + VAR_CLOSE  (key routes the editor click)
  return (opts && opts.markVars) ? (VAR_OPEN + key + VAR_SEP + s + VAR_CLOSE) : s
}

function renderNodes(nodes, ctx, opts) {
  let out = ''
  for (const n of nodes) {
    if (n.t === 'text') out += n.v
    else if (n.t === 'mustache') out += resolveMustache(n.v, ctx, opts)
    else if (n.t === 'compare') {
      const { field, op, value } = parseCompareArgs(n.args)
      const pass = evalCompare(getPath(ctx, field), op, value)
      if (pass) out += renderNodes(n.children, ctx, opts)
      else if (n.elseChildren) out += renderNodes(n.elseChildren, ctx, opts)
    }
  }
  return out
}

/** Render a Documint-style template fragment against a context object.
 *  opts.markVars wraps interpolated values in VAR_OPEN/VAR_CLOSE for editor highlighting. */
export function renderTemplate(tpl, ctx, opts) {
  if (!tpl) return ''
  const tokens = tokenize(tpl)
  const [nodes] = parseNodes(tokens, 0)
  let out = renderNodes(nodes, ctx || {}, opts)
  // tidy excess blank lines produced by stripped setup helpers / empty branches
  out = out.replace(/[ \t]+\n/g, '\n').replace(/\n{3,}/g, '\n\n')
  return out.trim()
}

/** Reconstruct template text from a node tree (inverse of tokenize/parseNodes).
 *  Used by the line-decomposition step to lift a {{#compare}} block's inner content into a line's
 *  text_template while keeping nested mustaches/compares verbatim. */
export function serializeNodes(nodes) {
  let out = ''
  for (const n of (nodes || [])) {
    if (n.t === 'text') out += n.v
    else if (n.t === 'mustache') out += '{{' + n.v + '}}'
    else if (n.t === 'compare') {
      out += '{{#compare ' + n.args + '}}' + serializeNodes(n.children)
      if (n.elseChildren) out += '{{else}}' + serializeNodes(n.elseChildren)
      out += '{{/compare}}'
    }
  }
  return out
}

// Export the tokenizer internals for the decomposition script (pure, no side effects).
export { tokenize, parseNodes }

/* ──────────────────────────────────────────────────────────────────────────
 * Line-level outline (Round 13 — SpecLink-style)
 * A spec section body is an ordered list of LINE objects:
 *   { id, level, kind:'clause'|'heading'|'block', numbered, text_template, text_override?,
 *     visibility_rule?, visible_override?, block_payload? }
 * ────────────────────────────────────────────────────────────────────────── */

/** Resolve a line's display text. An edited line (text_override set) is FROZEN — it ignores
 *  settings until reset; otherwise the template is resolved live against ctx. */
export function resolveLineText(line, ctx, opts) {
  if (!line) return ''
  const tpl = line.text_override != null ? line.text_override : (line.text_template || '')
  return renderTemplate(tpl, ctx, opts)
}

/** A line is visible if its manual override says so; else the conditional (visibility_rule) decides
 *  (null rule = always visible). */
export function isLineVisible(line, ctx) {
  if (!line) return false
  if (line.visible_override === true || line.visible_override === false) return line.visible_override
  return evalRule(line.visibility_rule, ctx)
}

function alphaLabel(n) { let s = '', x = n; while (x > 0) { x--; s = String.fromCharCode(97 + (x % 26)) + s; x = Math.floor(x / 26) } return s || 'a' }
function romanLabel(n) {
  const map = [[1000, 'm'], [900, 'cm'], [500, 'd'], [400, 'cd'], [100, 'c'], [90, 'xc'], [50, 'l'], [40, 'xl'], [10, 'x'], [9, 'ix'], [5, 'v'], [4, 'iv'], [1, 'i']]
  let s = '', x = n; for (const [v, sym] of map) { while (x >= v) { s += sym; x -= v } } return s || 'i'
}
// Legal-outline format per level: 0 = "1.", 1 = "a.", 2 = "i.", 3 = "(1)", 4 = "(a)".
const OUTLINE_FMT = [
  n => n + '.', n => alphaLabel(n) + '.', n => romanLabel(n) + '.', n => '(' + n + ')', n => '(' + alphaLabel(n) + ')',
]

/** Compute the outline label for each line, given ONLY the visible lines in order. Headings and
 *  non-numbered lines get '' (and do not consume a number or reset sibling counters). Position-aware:
 *  numbering at each level counts visible numbered clauses among its siblings; advancing a shallower
 *  level resets the deeper counters. Returns Map(line.id → label). */
export function computeOutlineLabels(visibleLines) {
  const labels = new Map()
  const counters = []
  for (const line of (visibleLines || [])) {
    if ((line.kind !== 'clause' && line.kind !== 'heading') || line.numbered === false) { labels.set(line.id, ''); continue }
    let L = line.level == null ? 0 : line.level
    if (L < 0) L = 0; if (L > 4) L = 4
    counters.length = L + 1            // truncate deeper counters (reset on advancing a shallower level)
    counters[L] = (counters[L] || 0) + 1
    labels.set(line.id, (OUTLINE_FMT[L] || OUTLINE_FMT[0])(counters[L]))
  }
  return labels
}

/** Resolve a section's lines for rendering: attaches { visible, text, textMarked, label } to each.
 *  `text` is clean (for flatten/PDF); `textMarked` carries variable-highlight sentinels (for the editor). */
export function resolveSectionLines(lines, ctx) {
  const resolved = (lines || []).map(line => ({
    ...line,
    visible: isLineVisible(line, ctx),
    text: resolveLineText(line, ctx),
    textMarked: resolveLineText(line, ctx, { markVars: true }),
  }))
  const labels = computeOutlineLabels(resolved.filter(r => r.visible))
  for (const r of resolved) r.label = labels.get(r.id) || ''
  return resolved
}

/** The table regions (@@MTX@@ … @@/MTX@@ — the Matrix of Responsibility) in a section's resolved
 *  lines, joined, markers kept so Markdown / the PDF draw them as tables. '' when the section has none.
 *  The outline spec carries these under the section's summary: the matrix is first verified at the
 *  Design Development outline spec, so the outline shows the real table, not just its summary. */
export function sectionTables(lines, ctx) {
  const out = []
  for (const r of resolveSectionLines(lines || [], ctx)) {
    if (!r.visible) continue
    const raw = r.kind === 'block' ? (r.block_payload && r.block_payload.raw_template) : null
    const txt = raw ? renderTemplate(raw, ctx) : String(r.text || '')
    const re = /@@MTX@@[\s\S]*?@@\/MTX@@/g
    let m
    while ((m = re.exec(txt))) out.push(m[0])
  }
  return out.join('\n')
}

/* ──────────────────────────────────────────────────────────────────────────
 * Schedule rendering (Documint repeating feature)
 *   cfg := { source_table, group_by?, filter?:{field,op,value}, columns:[{key,label}] }
 *   rows := array of Airtable records' `fields` objects (or plain row objects)
 * Returns { columns, groups:[{ group, items:[rowObj…] }] } for the table renderer.
 * ────────────────────────────────────────────────────────────────────────── */

export function renderSchedule(cfg, rows) {
  const columns = (cfg && cfg.columns) || []
  let list = Array.isArray(rows) ? rows.slice() : []
  if (cfg && cfg.filter && cfg.filter.field) {
    const { field, op, value } = cfg.filter
    list = list.filter(r => evalCompare(getPath(r, field), op, value))
  }
  if (cfg && cfg.group_by) {
    const order = []
    const byKey = {}
    for (const r of list) {
      const k = strip(getPath(r, cfg.group_by)) || '—'
      if (!(k in byKey)) { byKey[k] = []; order.push(k) }
      byKey[k].push(r)
    }
    return { columns, groups: order.map(k => ({ group: k, items: byKey[k] })) }
  }
  return { columns, groups: [{ group: null, items: list }] }
}

/* ──────────────────────────────────────────────────────────────────────────
 * Derived fields (recomputed, NOT stored).
 *
 * The Airtable Specifications record carried ~11 FORMULA fields that the master
 * rules reference (e.g. include_division_06, div_02_required, interior_finishes_
 * specifier). Those formulas are DERIVATIONS — in the Supabase model we store only
 * the raw inputs and recompute these here, so the SpecLink editor stays live (change
 * an input → derived gates update) and the PDF matches. Ported verbatim from the
 * Airtable formula definitions. Each *_required / include_* / *_section yields the
 * literal 'True'/'False' the master rules compare against.
 * ────────────────────────────────────────────────────────────────────────── */

export const SPECIFIER_NO_PACKAGE = 'TBD (no design package on the contract)'
export const SPECIFIER_NO_DESIGNER_ANSWER = 'TBD (outside interior designer question unanswered)'

/** Who selects the interior finishes, as a ROLE. pkg = the effective design package. */
export function interiorFinishesSpecifier(pkg, tpid) {
  if (pkg === 'Architecture + Interiors') return 'Architect'
  if (pkg === 'Essentials' || pkg === 'Premier') {
    if (tpid === 'Yes') return 'Interior Designer'
    if (tpid === 'No') return 'Owner'
    return SPECIFIER_NO_DESIGNER_ANSWER
  }
  return SPECIFIER_NO_PACKAGE
}

/**
 * Relevance rules that live in CODE rather than in spec_answer_fields.relevance_rule, used when the
 * catalog row has none. The outside-interior-designer question only applies when interiors are NOT in
 * our contract (Matt, 2026-09-24) — evaluated against the effective (contract-derived) package.
 */
export const SPEC_BUILTIN_RELEVANCE = {
  third_party_interior_designer: { op: '!=', field: 'contract_design_package', value: 'Architecture + Interiors' },
}

function nonEmptyVal(v) {
  if (v == null) return false
  if (typeof v === 'boolean') return v
  if (Array.isArray(v)) return v.filter(x => strip(x) !== '').length > 0
  return strip(v) !== ''
}
const TF = b => (b ? 'True' : 'False')

export function applyDerivations(ctx) {
  if (!ctx) return ctx
  const g = k => ctx[k]
  const has = (v, needle) => containsVal(v, needle)

  // 04 CMU Section ← 04 Masonry Scope
  ctx['04_cmu_section'] = TF(
    has(g('04_masonry_scope'), 'CMU Walls with Exposed Visible Finishes') ||
    has(g('04_masonry_scope'), 'CMU Walls Covered with Cladding'))

  // Div 02 Required ← project type + (demolition area OR an explicit "existing structure to demo" answer).
  // Additions/remodels always need it; new builds need it when there's an existing structure to take down.
  const ptype = strip(g('project_type')).toLowerCase()
  const isNew = ptype === 'new' || ptype.startsWith('new ')   // the Project record uses "New Build"
  const demo = Number(g('total_building_demolition_area')) || 0
  const demoStruct = g('existing_structure_to_demo')
  const demoStructYes = demoStruct === true || demoStruct === 'true' || strip(demoStruct).toLowerCase() === 'yes'
  ctx['div_02_required'] = TF(
    ptype === 'addition' || ptype === 'remodel' || ptype === 'remodel and addition' ||
    (isNew && (demo > 0 || demoStructYes)))

  // Blower-door test (01-TAB) ← a new build, or a full envelope replacement (effectively a new
  // envelope worth testing for air-tightness). full_envelope_replacement is a Yes/No answer.
  ctx['blower_door_test_required'] = TF(isNew || strip(g('full_envelope_replacement')).toLowerCase() === 'yes')

  // Division 05 + Misc Metals ← steel / stairs / guards / handrails / chimney shroud
  const d05 = [g('assembled_steel_stairs'), g('guard_types'), g('handrail_types'),
    g('chimney_shroud_model'), g('chimney_shroud_finish')].some(nonEmptyVal)
  ctx['include_misc_metals'] = TF(d05)
  ctx['include_division_05'] = TF(nonEmptyVal(g('structural_steel')) || d05)

  // Division 06 + Wood Framing ← structural / finish wood scope
  const sws = g('06_structural_wood_scope'), fws = g('06_finish_wood_scope')
  ctx['include_division_06'] = TF(nonEmptyVal(sws) || nonEmptyVal(fws))
  ctx['include_wood_framing'] = TF(
    has(sws, 'Light Wood Framing') || has(sws, 'Heavy Timber Framing') || has(sws, 'Wood Stairs'))

  // Fire sprinkler ← Fire Suppression Scope
  ctx['include_fire_sprinkler_system'] = TF(
    has(g('fire_suppression_scope'), 'New Fire Sprinkler System') ||
    has(g('fire_suppression_scope'), 'Existing Fire Sprinkler System Modifications'))

  // Furring strips ← Exterior Siding Scope ; Misc plumbing ← Exterior Improvements Scope
  ctx['include_furring_strips'] = TF(has(g('exterior_siding_scope'), 'Plank Siding'))
  // 22-MPS carries an irrigation note (landscaping) and/or a leak-detection note. Drive it by the same
  // answers its body uses: landscaping in exterior improvements OR Leak Detection in the plumbing scope.
  ctx['include_misc_plumbing_systems'] = TF(
    has(g('exterior_improvements_scope'), 'Landscaping') ||
    has(g('plumbing_scope'), 'Leak Detection'))

  // Special construction ← Solar Energy System Scope
  ctx['special_construction_section'] = TF(nonEmptyVal(g('solar_energy_system_scope')))

  // Interior Finishes Specifier (role, not a name) ← design package + third-party designer.
  // The package comes from the CONTRACT when buildContext was handed one (contract_package_source set:
  // the executed proposal's Selected Package, or an Interior Architecture phase — api/_specContract.js);
  // the hand answer is only the fallback. With interiors in our contract the outside-designer question
  // doesn't apply (it is hidden, SPEC_BUILTIN_RELEVANCE), so the Architect selects. When something is
  // missing the value SAYS what is missing instead of printing "Unspecified" (Matt, 2026-09-24).
  ctx['interior_finishes_specifier'] = interiorFinishesSpecifier(
    strip(g('contract_design_package')), strip(g('third_party_interior_designer')))

  return ctx
}

/* ──────────────────────────────────────────────────────────────────────────
 * Build the resolution context for a project from its answers + project header.
 *   answers     — spec_project_answers.answers (flat { documint_key: value })
 *   projectHdr  — { project_name, project_number, project_state, project_address,
 *                   client_organization_name, contract_design_package,
 *                   contract_package_source }  (from the Airtable project/concept; the two
 *                   contract_* keys come from api/_specContract.js and override the hand answer)
 *   extra       — { architect_name, issue_date, interior_finishes_specifier, … }
 * Injects BOTH flat keys and the Documint project_concepts shape so seed fragments
 * referencing either form resolve unchanged.
 * ────────────────────────────────────────────────────────────────────────── */

export function buildContext(answers, projectHdr, extra) {
  const p = projectHdr || {}
  const a = answers || {}
  // Project context may arrive via projectHdr OR be captured in the answers snapshot
  // (the import stores project_type / demolition area / state alongside the inputs).
  const pick = (hdr, key) => (hdr != null ? hdr : a[key])
  const projType = pick(p.project_type, 'project_type')
  const buildingType = pick(p.building_type, 'building_type')
  const demoArea = pick(p.total_building_demolition_area, 'total_building_demolition_area')
  const projState = pick(p.project_state, 'project_state')
  const projName = pick(p.project_name, 'project_name')
  const projNumber = pick(p.project_number, 'project_number')
  const projAddress = pick(p.project_address, 'project_address')
  const orgName = pick(p.client_organization_name, 'client_organization_name')
  // Design package from the CONTRACT (api/_specContract.js) overrides the hand answer; the hand answer
  // is kept as contract_design_package_answer so the editor can show a disagreement.
  const contractPkg = p.contract_design_package || null
  const handPkg = a.contract_design_package == null ? null : a.contract_design_package
  const pkgFields = contractPkg
    ? { contract_design_package: contractPkg, contract_package_source: p.contract_package_source || 'contract', contract_design_package_answer: handPkg }
    : { contract_package_source: handPkg ? 'answer' : null, contract_design_package_answer: handPkg }
  const ctx = Object.assign({}, a, extra || {}, pkgFields, {
    // project-context inputs needed by derivations (project type / demolition area)
    project_type: projType,
    building_type: buildingType,
    total_building_demolition_area: demoArea,
    project: {
      project_name: projName, project_number: projNumber, project_state: projState,
      project_address: projAddress, organization_name: orgName,
      project_type: projType, building_type: buildingType,
    },
    // Documint nested shape: project_concepts.0.project.0.{…} — templates reference project_type /
    // building_type via this path, so inject them here too (otherwise they render blank → "the of a").
    project_concepts: [{
      project: [{
        project_name: projName, project_number: projNumber, project_state: projState,
        project_address: projAddress, project_type: projType, building_type: buildingType,
        client: [{ organization_name: orgName }],
      }],
    }],
  })
  return applyDerivations(ctx)
}

export default {
  getPath, evalCompare, evalRule, parseCompareArgs,
  renderTemplate, renderSchedule, buildContext, applyDerivations,
  serializeNodes, tokenize, parseNodes,
  resolveLineText, isLineVisible, computeOutlineLabels, resolveSectionLines,
}
